/**
 * CallTrackingMetrics Region Selector (Story 22.3, AC #2)
 *
 * **NEW for CTM — no analogue in prior API-key adapters** (Ahrefs 20.1,
 * Semrush 20.4, Moz 20.7, CallRail 22.1). CTM is the FIRST adapter in our
 * library with a region selector.
 *
 * CTM operates a US region (`api.calltrackingmetrics.com`) and a separate
 * EU region (`api.eu.calltrackingmetrics.com`) for GDPR data residency.
 * Per the [CTM Data Protection Addendum 2025](https://www.calltrackingmetrics.com/legal/data-protection-addendum-2025/)
 * verified 2026-05-15, CTM offers GDPR-compliant call tracking and
 * references Standard Contractual Clauses for EU customer data — but the
 * public `api.eu.calltrackingmetrics.com` hostname is NOT corroborated
 * by web search (verified 2026-05-15 on www.ctm.com/security/gdpr/ —
 * GDPR compliance is documented but the EU hostname is NOT publicly
 * confirmed).
 *
 * **Round 2 Pre-mortem B defense:** the adapter treats the EU hostname
 * as catalogue-claimed but runtime-probe-verified. The connect-time
 * probe (per AC #2) attempts `https://api.eu.calltrackingmetrics.com/
 * api/v1/accounts.json` with the credentials; if DNS fails
 * (`ENOTFOUND`) or returns 5xx, the adapter falls back to US and
 * emits a `ctm_eu_region_probe_failed` structured-log warning. If the
 * probe succeeds, the integration is stamped with `metadata.region:
 * "eu"` and all subsequent calls go to the EU hostname.
 *
 * The default region is `"us"` per Round 4 ADR-D.
 */

/**
 * Frozen tuple of CTM region identifiers. Adding a new region (e.g. APAC)
 * is additive — append the new value at the end of the tuple and
 * register its base URL in `baseUrlFor`.
 */
export const CTM_REGION_VALUES = ["us", "eu"] as const;

/** Type of CTM region identifiers. */
export type CtmRegion = (typeof CTM_REGION_VALUES)[number];

/** Default region per Round 4 ADR-D. */
export const CTM_DEFAULT_REGION: CtmRegion = "us";

/** US base URL — frozen. */
export const CTM_API_BASE_US = "https://api.calltrackingmetrics.com/api/v1" as const;

/**
 * EU base URL — catalogue-claimed, runtime-probed at connect time per
 * Round 2 Pre-mortem B. The hostname `api.eu.calltrackingmetrics.com`
 * is NOT publicly confirmed; the adapter probes at connect time and
 * falls back to US on DNS/5xx failure.
 */
export const CTM_API_BASE_EU = "https://api.eu.calltrackingmetrics.com/api/v1" as const;

/**
 * Return the v1 base URL for the given region. Throws `TypeError`
 * synchronously for any unknown region identifier — exhaustiveness gate
 * against typos in the request path or KV side-channel.
 *
 * @param region - One of `"us"` or `"eu"`.
 * @returns The frozen v1 base URL string (no trailing slash).
 * @throws `TypeError` if `region` is not a known `CtmRegion`.
 */
export function baseUrlFor(region: CtmRegion | string): string {
  if (region === "us") return CTM_API_BASE_US;
  if (region === "eu") return CTM_API_BASE_EU;
  throw new TypeError(
    `baseUrlFor: unknown CTM region "${region}". Expected one of: ${CTM_REGION_VALUES.join(", ")}.`,
  );
}

/**
 * Type guard: is the given string a valid `CtmRegion`?
 *
 * Used by the route handler to coerce request-body `region` strings into
 * the union type before they reach `baseUrlFor`.
 */
export function isCtmRegion(value: unknown): value is CtmRegion {
  return (
    typeof value === "string" &&
    (CTM_REGION_VALUES as ReadonlyArray<string>).includes(value)
  );
}
